import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Trash2, LogOut } from 'lucide-react';

export const CartDrawer = ({
  isOpen,
  onClose,
  hardCart,
  softCart,
  onRemoveFromCart,
  onAddToCart,
  onCheckout,
  onEndSession,
}) => {
  const cartTotal = hardCart.reduce((sum, p) => sum + (p.selling_price || p.price || 0), 0);
  const itemsTriedNotInCart = softCart.filter(
    (p) => !hardCart.find((h) => (h.product_id || h.id) === (p.product_id || p.id))
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-sm bg-white shadow-2xl z-50 flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-semibold text-lg text-slate-800">Your Cart ({hardCart.length})</h2>
              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full" data-testid="close-cart-button">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {hardCart.length === 0 ? (
                <div className="text-center py-12" data-testid="empty-cart-message">
                  <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Your cart is empty</p>
                  <p className="text-slate-400 text-sm mt-1">Try on products and add them here</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="cart-items-list">
                  {hardCart.map((product) => (
                    <div
                      key={product.product_id || product.id}
                      className="flex gap-3 p-3 bg-slate-50 rounded-xl"
                      data-testid={`cart-item-${product.product_id || product.id}`}
                    >
                      <img
                        src={product.thumbnail_url || product.images?.[0]?.thumbnail_url || product.images?.[0]?.image_url || product.primary_image_url}
                        alt={product.product_name || product.name}
                        className="w-16 h-20 object-cover rounded-lg"
                        loading="lazy"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-slate-800 text-sm truncate">
                          {product.product_name || product.name}
                        </h3>
                        <p className="text-blue-600 font-semibold text-sm mt-1">
                          ₹{(product.selling_price || product.price || 0).toLocaleString()}
                        </p>
                      </div>
                      <button
                        onClick={() => onRemoveFromCart(product)}
                        className="p-1.5 text-slate-400 hover:text-red-500 self-start hover:bg-red-50 rounded-full transition-colors"
                        data-testid={`remove-item-${product.product_id || product.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {itemsTriedNotInCart.length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <h3 className="text-sm font-medium text-slate-500 mb-3">Items You Tried On</h3>
                  <div className="space-y-2">
                    {itemsTriedNotInCart.map((product) => (
                      <div key={product.product_id || product.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                        <img
                          src={product.thumbnail_url || product.images?.[0]?.thumbnail_url || product.images?.[0]?.image_url || product.primary_image_url}
                          alt={product.product_name || product.name}
                          className="w-10 h-12 object-cover rounded"
                          loading="lazy"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-700 truncate">{product.product_name || product.name}</p>
                          <p className="text-xs text-slate-500">₹{(product.selling_price || product.price || 0).toLocaleString()}</p>
                        </div>
                        <button
                          onClick={() => onAddToCart(product)}
                          className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-medium hover:bg-blue-100 transition-colors"
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50">
              {hardCart.length > 0 && (
                <div className="flex items-center justify-between mb-4" data-testid="cart-total">
                  <span className="text-slate-600">Total</span>
                  <span className="text-xl font-bold text-slate-800">₹{cartTotal.toLocaleString()}</span>
                </div>
              )}
              <div className="space-y-2">
                {hardCart.length > 0 && (
                  <button
                    onClick={onCheckout}
                    className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                    data-testid="checkout-button"
                  >
                    Proceed to Checkout
                  </button>
                )}
                <button
                  onClick={onEndSession}
                  className="w-full py-3 bg-slate-100 text-slate-600 rounded-xl font-medium hover:bg-slate-200 transition-colors flex items-center justify-center gap-2"
                  data-testid="end-session-button"
                >
                  <LogOut className="w-4 h-4" />
                  End Session
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
